import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { eventsAPI, workspaceAPI } from '../services/api';
import LexaFAB from './LexaFAB';

const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';
const PLAN_LEVELS = { starter: 1, pro: 2, enterprise: 3 };

// NAV PRINCIPALE — structurée en sections affichées dans la sidebar.
// Chaque section a un titre uppercase + items. Les badges (Pro/Enterprise) sont
// affichés mais l'accès reste géré par PlanGate côté routing.
const NAV_SECTIONS = [
  {
    title: 'Production',
    items: [
      { path: '/dashboard',     label: 'Dashboard',      icon: 'dashboard', minPlan: 'starter' },
      { path: '/dossiers',      label: 'Dossiers',       icon: 'stack',     minPlan: 'starter' },
      { path: '/signatures',    label: 'Signatures',     icon: 'pen',       minPlan: 'starter' },
      { path: '/emails',        label: 'Suivi des emails', icon: 'mail',     minPlan: 'starter' },
      { path: '/jal',           label: 'Annonces JAL',   icon: 'news',      minPlan: 'starter' },
      { path: '/dossiers/inpi', label: 'Suivi INPI',     icon: 'radar',     minPlan: 'starter' },
      { path: '/formalistes',   label: 'Mes équipes',    icon: 'users',     minPlan: 'starter' },
    ],
  },
  {
    title: 'Analyse',
    items: [
      { path: '/pilotage',      label: 'Pilotage',       icon: 'chart',     minPlan: 'starter' },
    ],
  },
  {
    title: 'Infrastructure',
    items: [
      { path: '/config',        label: 'Connecteurs',    icon: 'plug',      minPlan: 'starter' },
      { path: '/developpeurs',  label: 'API & Webhooks', icon: 'code',      minPlan: 'enterprise', badge: 'Enterprise' },
    ],
  },
];

// Pour conserver la rétro-compatibilité avec d'éventuels imports / autres composants
const NAV_PRIMARY = NAV_SECTIONS[0].items;

// MENU AVATAR — réduit aux pages compte/administratives.
const NAV_SECONDARY = [
  { section: 'Compte' },
  { path: '/mon-compte',    label: 'Mon compte',          minPlan: 'starter' },
  { path: '/branding',      label: 'Identité visuelle',   minPlan: 'starter' },
  { path: '/facturation',   label: 'Facturation',         minPlan: 'starter' },
  { path: '/activite',      label: 'Activité',            minPlan: 'starter' },
  { path: '/notifications', label: 'Notifications',       minPlan: 'starter' },
];

const UPGRADE_MSG = {
  starter: { title: 'Passer à Pro', sub: 'Débloquez le pilotage juridique et la scorecard qualité.' },
  pro:     { title: 'Passer à Enterprise', sub: 'Forecasting, benchmarking, API avancée et connecteurs Zendesk/Intercom.' },
};

const NOTIF_TYPE_META = {
  mention:          { icon: '🔔', label: 'vous a mentionné', color: '#d9a847' },
  new_message:      { icon: '💬', label: 'a écrit un message', color: '#16a34a' },
  client_reply:     { icon: '📨', label: 'a répondu', color: '#3b82f6' },
  status_change:    { icon: '✓',  label: 'a changé le statut', color: '#7c3aed' },
  doc_uploaded:     { icon: '📄', label: 'a déposé un document', color: '#0ea5e9' },
  default:          { icon: '•',  label: '', color: '#9ca3af' },
};

const _initials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

// Couleur d'avatar dérivée d'un hash du nom — pour cohérence visuelle d'une session à l'autre
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #ff9a6d, #ff6b9d)', // corail-rose (ex Rita)
  'linear-gradient(135deg, #6dd5ff, #6d8aff)', // cyan-bleu (ex Fati)
  'linear-gradient(135deg, #a3e635, #65a30d)', // vert-lime (ex Lina)
  'linear-gradient(135deg, #c084fc, #9333ea)', // violet-mauve
  'linear-gradient(135deg, #fcd34d, #d97706)', // ambre-orange
  'linear-gradient(135deg, #5eead4, #0d9488)', // teal-émeraude
];
const _avatarColor = (name) => {
  if (!name) return AVATAR_GRADIENTS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[h];
};

const _relativeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'à l\'instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

// ============================================================================
// PALETTE — Espresso profond + or signal Lexback
// ============================================================================
const C = {
  // Sidebar (espresso profond avec dégradé radial appliqué inline)
  sbBg:          '#1f1612',
  sbText:        'rgba(255,255,255,0.72)',
  sbTextDim:     'rgba(255,255,255,0.45)',
  sbTextFaded:   'rgba(255,255,255,0.4)',
  sbWhite:       '#ffffff',
  sbBorder:      'rgba(255,255,255,0.08)',
  sbBorderSoft:  'rgba(255,255,255,0.06)',
  sbActive:      'rgba(217,168,71,0.14)',
  sbActiveStrong:'rgba(217,168,71,0.18)',
  sbHover:       'rgba(255,255,255,0.05)',

  // Contenu principal
  bg:            '#fafaf8',
  card:          '#ffffff',
  border:        'rgba(0,0,0,0.08)',
  borderLt:      'rgba(0,0,0,0.04)',
  text:          '#1d1d1f',
  text2:         '#424245',
  text3:         '#8b8576',

  // Or signal — pointe vers --brand-primary pour suivre le branding du cabinet
  gold:          'var(--brand-primary, #d9a847)',
  goldLight:     'var(--brand-primary, #e6c47a)',
  goldBg:        'var(--brand-primary-bg, rgba(217,168,71,0.12))',
  goldBgStrong:  'var(--brand-primary-bg, rgba(217,168,71,0.2))',
  goldText:      'var(--brand-primary, #8a7234)',
};

const SIDEBAR_WIDTH = 240;
const TOPBAR_HEIGHT = 56;

const S = {
  // ========== SIDEBAR ==========
  sidebar: {
    width: `${SIDEBAR_WIDTH}px`,
    background: `radial-gradient(ellipse at top, #2a1f18 0%, ${C.sbBg} 70%)`,
    boxShadow: 'inset 0 0 60px rgba(0,0,0,0.4)',
    padding: '18px 16px',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    overflowY: 'auto',
    zIndex: 40,
    fontFamily: 'var(--font-body)',
    letterSpacing: '-0.011em',
  },
  sbBrand: { display: 'flex', alignItems: 'center', gap: '10px', padding: '0 4px 16px', borderBottom: `0.5px solid ${C.sbBorder}` },
  sbMark: { width: '28px', height: '28px', background: C.gold, color: '#1a1410', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '13px', letterSpacing: '-0.03em', flexShrink: 0 },
  sbBrandText: { lineHeight: 1.2, flex: 1, minWidth: 0 },
  sbBrandName: { fontSize: '13px', color: C.sbWhite, fontWeight: 600, letterSpacing: '-0.022em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sbBrandTag: { fontSize: '10px', color: C.sbTextDim, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '2px', fontWeight: 500 },

  sbSection: { marginTop: '16px' },
  sbSectionTitle: { fontSize: '9.5px', color: C.sbTextFaded, letterSpacing: '0.14em', fontWeight: 600, textTransform: 'uppercase', padding: '0 8px', marginBottom: '6px' },

  sbLinkBase: { display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, letterSpacing: '-0.01em', marginBottom: '1px', position: 'relative', cursor: 'pointer', textDecoration: 'none', transition: 'background 0.12s, color 0.12s' },
  sbCount: { marginLeft: 'auto', fontSize: '11px', color: C.sbTextDim, fontVariantNumeric: 'tabular-nums' },
  sbActiveBar: { content: '""', position: 'absolute', left: '-16px', top: '50%', transform: 'translateY(-50%)', width: '3px', height: '16px', background: C.gold, borderRadius: '0 3px 3px 0' },

  sbStaffRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', color: C.sbText, fontSize: '12.5px', fontWeight: 500 },
  sbStaffAvatar: { width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.1)' },
  sbStaffOnline: { position: 'absolute', bottom: '-1px', right: '-1px', width: '7px', height: '7px', borderRadius: '50%', background: '#4ade80', border: `1.5px solid ${C.sbBg}` },
  sbStaffIris: { width: '20px', height: '20px', borderRadius: '5px', background: C.sbActiveStrong, border: `0.5px solid rgba(217,168,71,0.35)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sbStaffIrisBadge: { fontSize: '8.5px', color: C.gold, background: 'rgba(217,168,71,0.14)', padding: '1px 5px', borderRadius: '3px', fontWeight: 600, letterSpacing: '0.04em' },

  sbBottom: { marginTop: 'auto', paddingTop: '12px', borderTop: `0.5px solid ${C.sbBorder}` },
  sbUser: { display: 'flex', alignItems: 'center', gap: '9px', padding: '6px 4px', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.12s' },
  sbUserAvatar: { width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg, #8b6dff, #ff6dd4)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '11px', flexShrink: 0 },
  sbUserInfo: { flex: 1, minWidth: 0, lineHeight: 1.2 },
  sbUserName: { fontSize: '12.5px', color: C.sbWhite, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sbUserMeta: { fontSize: '10.5px', color: C.sbTextDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

  // ========== TOPBAR ==========
  topbar: {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    background: C.card,
    borderBottom: `0.5px solid ${C.border}`,
    padding: '0 24px',
    height: `${TOPBAR_HEIGHT}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '14px',
    fontFamily: 'var(--font-body)',
    letterSpacing: '-0.011em',
  },
  tbBellWrap: { position: 'relative', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', cursor: 'pointer', color: C.text2, transition: 'background 0.12s', background: 'transparent', border: 'none', padding: 0 },
  tbAvatarBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 10px 4px 4px', borderRadius: '999px', border: `0.5px solid ${C.border}`, background: C.card, cursor: 'pointer', transition: 'all 0.12s' },
  tbAvatarLabel: { fontSize: '12.5px', fontWeight: 500, color: C.text, maxWidth: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  tbAvatarCircle: { width: '26px', height: '26px', borderRadius: '50%', background: C.text, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, fontSize: '11px', flexShrink: 0 },

  // ========== NOTIF PANEL ==========
  notifPanel: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '380px', maxWidth: 'calc(100vw - 32px)', background: C.card, border: `0.5px solid ${C.border}`, borderRadius: '12px', boxShadow: '0 12px 32px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)', zIndex: 100, animation: 'lbMenuIn 0.16s ease', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '520px' },
  notifHeader: { padding: '12px 16px', borderBottom: `0.5px solid ${C.borderLt}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  notifTitle: { fontSize: '14px', fontWeight: 600, color: C.text },
  notifMarkAll: { background: 'transparent', border: 'none', color: C.gold, fontSize: '11.5px', fontWeight: 500, cursor: 'pointer', padding: 0 },
  notifList: { flex: 1, overflowY: 'auto', minHeight: 0 },
  notifEmpty: { padding: '32px 16px', textAlign: 'center', color: C.text3, fontSize: '13px' },
  notifItem: { display: 'flex', gap: '10px', padding: '12px 16px', cursor: 'pointer', borderBottom: `0.5px solid ${C.borderLt}`, transition: 'background 0.15s' },
  notifFooter: { padding: '10px 16px', borderTop: `0.5px solid ${C.borderLt}`, textAlign: 'center', flexShrink: 0 },

  // ========== MENU AVATAR ==========
  menu: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '280px', background: C.card, border: `0.5px solid ${C.border}`, borderRadius: '12px', boxShadow: '0 12px 32px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)', padding: '8px', display: 'flex', flexDirection: 'column', gap: '1px', zIndex: 100, animation: 'lbMenuIn 0.16s ease' },
  menuHeader: { padding: '10px 12px 12px', borderBottom: `0.5px solid ${C.borderLt}`, marginBottom: '4px' },
  menuWsName: { fontSize: '13px', fontWeight: 500, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  menuWsPlan: { fontSize: '11px', color: C.text3, marginTop: '2px' },
  menuSection: { fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.text3, padding: '10px 12px 4px' },
  menuItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '7px', fontSize: '13px', textDecoration: 'none', transition: 'background 0.12s', cursor: 'pointer' },
  menuUpgrade: { margin: '6px 4px', padding: '12px', borderRadius: '8px', background: C.goldBg, border: `0.5px solid rgba(217,168,71,0.25)`, cursor: 'pointer', transition: 'background 0.12s' },
  menuUpgradeTitle: { fontSize: '12.5px', fontWeight: 600, color: C.goldText },
  menuUpgradeSub: { fontSize: '11px', color: C.text3, marginTop: '3px', lineHeight: 1.4 },
  menuLogout: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '7px', fontSize: '13px', color: C.text2, background: 'transparent', border: 'none', cursor: 'pointer', marginTop: '4px', transition: 'background 0.12s, color 0.12s', fontFamily: 'inherit' },

  badge:      { fontSize: '9.5px', padding: '2px 7px', background: C.goldBg, color: C.gold, borderRadius: '999px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' },
  badgeActive:{ fontSize: '9.5px', padding: '2px 7px', background: 'rgba(217,168,71,0.20)', color: '#6b4f00', borderRadius: '999px', fontWeight: 600 },
  badgeOrphan:{ fontSize: '9px', padding: '2px 6px', background: '#b3261e', color: '#fff', borderRadius: '999px', fontWeight: 700, minWidth: '18px', textAlign: 'center' },

  // ========== FAB + DRAWER ==========
  fab: { position: 'fixed', bottom: '28px', right: '28px', width: '56px', height: '56px', borderRadius: '50%', background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border: 'none', cursor: 'pointer', boxShadow: '0 8px 24px rgba(217,168,71,0.35), 0 2px 6px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '22px', zIndex: 100, transition: 'transform 0.18s, box-shadow 0.18s' },
  fabLabel: { position: 'absolute', right: '70px', top: '50%', transform: 'translateY(-50%)', background: C.text, color: '#fff', padding: '7px 12px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 500, whiteSpace: 'nowrap', fontFamily: 'var(--font-body)', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.18s' },
  drawerBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, animation: 'lbFadeIn 0.18s ease' },
  drawer: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(720px, 95vw)', background: C.bg, boxShadow: '-12px 0 32px rgba(0,0,0,0.12)', zIndex: 201, display: 'flex', flexDirection: 'column', animation: 'lbSlideIn 0.22s cubic-bezier(0.16,1,0.3,1)' },
  drawerHeader: { padding: '18px 24px', borderBottom: `0.5px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.card },
  drawerTitle: { fontFamily: 'var(--font-title)', fontSize: '18px', fontWeight: 600, color: C.text, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '10px' },
  drawerClose: { background: 'transparent', border: 'none', color: C.text3, cursor: 'pointer', fontSize: '22px', padding: '4px 10px', borderRadius: '6px', lineHeight: 1 },
  drawerBody: { flex: 1, overflowY: 'auto', padding: '20px 24px' },
};

// ============================================================================
// ICÔNES SIDEBAR (SVG inline pour éviter de charger une lib)
// ============================================================================
const Icon = ({ name, size = 16 }) => {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (name) {
    case 'stack':
      return <svg {...common}><path d="M12 2 L2 7 L12 12 L22 7 Z" /><path d="M2 17 L12 22 L22 17" /><path d="M2 12 L12 17 L22 12" /></svg>;
    case 'dashboard':
      return <svg {...common}><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></svg>;
    case 'chart':
      return <svg {...common}><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" /></svg>;
    case 'plug':
      return <svg {...common}><path d="M9 2v6M15 2v6M6 8h12v4a6 6 0 0 1-12 0V8zM12 18v4" /></svg>;
    case 'code':
      return <svg {...common}><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>;
    case 'radar':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></svg>;
    case 'news':
      return <svg {...common}><path d="M4 4h16v16H4z" /><path d="M8 8h8M8 12h8M8 16h5" /></svg>;
    case 'pen':
      return <svg {...common}><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" /></svg>;
    case 'users':
      return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'sparkles':
      return <svg {...common}><path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" /></svg>;
    case 'bell':
      return <svg {...common}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>;
    case 'mail':
      return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>;
    default:
      return null;
  }
};

// ============================================================================
// COMPOSANT
// ============================================================================
export default function Layout({ children }) {
  const { user, logout, impersonating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const plan = user?.workspace?.plan || 'starter';
  const planLevel = PLAN_LEVELS[plan] || 1;

  // Connecteurs actifs
  const [activeConnectors, setActiveConnectors] = useState({ aircall: false, zendesk: false });
  useEffect(() => {
    let cancelled = false;
    workspaceAPI.connectors()
      .then(r => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        const out = { aircall: false, zendesk: false };
        for (const c of list) {
          if (c.is_active) {
            if (c.type === 'aircall') out.aircall = true;
            if (c.type === 'zendesk') out.zendesk = true;
          }
        }
        setActiveConnectors(out);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Polling orphelins
  const [orphanCounts, setOrphanCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    const fetchOrphans = () => {
      eventsAPI.orphansCount()
        .then(r => { if (!cancelled) setOrphanCounts(r.data || {}); })
        .catch(() => {});
    };
    fetchOrphans();
    const interval = setInterval(fetchOrphans, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Équipe Lexback dynamique (endpoint pas encore en prod → fail silencieux)
  const [lexbackStaff, setLexbackStaff] = useState([]);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem('lexback_token');
        const res = await fetch(`${API_URL}/api/team/lexback-staff`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) setLexbackStaff(data);
      } catch (e) { /* silencieux : endpoint à venir */ }
    };
    fetchStaff();
    const interval = setInterval(fetchStaff, 120000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  // Compteurs dossiers (réutilisés pour les badges sidebar)
  const [dossiersCount, setDossiersCount] = useState(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('lexback_token');
        const res = await fetch(`${API_URL}/api/dossiers/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setDossiersCount(data?.total ?? null);
      } catch (e) { /* silencieux */ }
    };
    fetchCount();
    return () => { cancelled = true; };
  }, [user]);

  // Notifications
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('lexback_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [listRes, countRes] = await Promise.all([
        fetch(`${API_URL}/api/notifications?limit=15`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/notifications/count`, { headers }).then(r => r.ok ? r.json() : { unread: 0 }),
      ]);
      setNotifs(Array.isArray(listRes) ? listRes : []);
      setUnreadCount(countRes?.unread || 0);
    } catch (e) {}
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchNotifications();
    const interval = setInterval(() => { if (!cancelled) fetchNotifications(); }, 60000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const markNotifRead = async (id) => {
    try {
      const token = localStorage.getItem('lexback_token');
      await fetch(`${API_URL}/api/notifications/${id}/read`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (e) {}
  };

  const markAllNotifRead = async (e) => {
    e?.stopPropagation();
    try {
      const token = localStorage.getItem('lexback_token');
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const now = new Date().toISOString();
      setNotifs(prev => prev.map(n => n.read_at ? n : { ...n, read_at: now }));
      setUnreadCount(0);
    } catch (err) {}
  };

  const handleNotifClick = (notif) => {
    if (!notif.read_at) markNotifRead(notif.id);
    setNotifOpen(false);
    if (notif.dossier_id) navigate(`/dossiers?open=${notif.dossier_id}`);
  };

  // Menu avatar
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen && !notifOpen) return;
    const onClickOutside = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifOpen && notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setMenuOpen(false); setNotifOpen(false); }
    };
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, notifOpen]);

  const initials = user ? `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase() : 'U';
  // Branding du cabinet : prime sur le nom workspace par défaut
  const { branding } = useBranding();
  const workspaceName = branding?.nom_commercial || user?.workspace?.name || user?.prenom || 'Mon espace';
  const planLabel = { starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' }[plan] || 'Starter';

  // ── Styles dérivés du branding ──
  // On surcharge dynamiquement les morceaux les plus visibles de la sidebar
  // (background, couleur active or, mark) pour qu'ils suivent la couleur
  // primaire/secondaire choisie par le cabinet sur /branding.
  const brandPrimary   = branding?.couleur_primaire   || C.gold;
  const brandSecondary = branding?.couleur_secondaire || C.sbBg;
  // Convertit un hex en rgba (pour les fonds atténués comme sbActive)
  const hexToRgba = (hex, a) => {
    if (!hex || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return `rgba(217,168,71,${a})`;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const r = parseInt(h.slice(0,2),16), g = parseInt(h.slice(2,4),16), b = parseInt(h.slice(4,6),16);
    return `rgba(${r},${g},${b},${a})`;
  };
  const sidebarStyle = {
    ...S.sidebar,
    // Couleur unie (pas de dégradé) — fidèle à la `couleur_secondaire` choisie
    background: brandSecondary,
    boxShadow: 'none',
  };
  const sbMarkStyle = { ...S.sbMark, background: brandPrimary, color: '#fff' };
  const sbActiveBg = hexToRgba(brandPrimary, 0.18);
  const sbActiveBar = { ...S.sbActiveBar, background: brandPrimary };

  const handleLogout = () => { logout(); navigate('/login'); };

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  return (
    <>
      <style>{`
        @keyframes lbFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lbSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes lbMenuIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div style={{
        display: 'flex',
        minHeight: '100vh',
        background: C.bg,
        fontFamily: 'var(--font-body)',
        letterSpacing: '-0.011em',
        paddingTop: impersonating ? '40px' : 0,
      }}>

        {/* ============================================================ */}
        {/* SIDEBAR (espresso profond, fixe à gauche)                    */}
        {/* ============================================================ */}
        <aside style={sidebarStyle}>

          {/* Brand — logo et nom commercial du cabinet (configurable via /branding) */}
          <Link to="/dossiers" style={{ ...S.sbBrand, textDecoration: 'none' }}>
            {branding?.logo_url ? (
              <img
                src={branding.logo_url}
                alt={workspaceName}
                style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', background: '#fff' }}
              />
            ) : (
              <div style={sbMarkStyle}>
                {(workspaceName || 'L').charAt(0).toUpperCase()}
              </div>
            )}
            <div style={S.sbBrandText}>
              <div style={S.sbBrandName}>{workspaceName}</div>
              {!branding?.hide_lexback_branding && (
                <div style={S.sbBrandTag}>par Lexback</div>
              )}
            </div>
          </Link>

          {/* Sections de navigation */}
          {NAV_SECTIONS.map((section) => {
            // Pour la section Production : ajouter dynamiquement Aircall/Zendesk si connectés
            let items = section.items;
            if (section.title === 'Production') {
              const extras = [];
              if (activeConnectors.aircall) extras.push({ path: '/aircall', label: 'Aircall', icon: 'news', minPlan: 'starter' });
              if (activeConnectors.zendesk) extras.push({ path: '/zendesk', label: 'Zendesk', icon: 'news', minPlan: 'starter' });
              items = [...items, ...extras];
            }

            return (
              <div key={section.title} style={S.sbSection}>
                <div style={S.sbSectionTitle}>{section.title}</div>
                {items.map((item) => {
                  const itemLevel = PLAN_LEVELS[item.minPlan] || 1;
                  const locked = planLevel < itemLevel;
                  const active = isActive(item.path);
                  const orphanCount =
                    item.path === '/aircall' ? orphanCounts.aircall :
                    item.path === '/zendesk' ? orphanCounts.zendesk : 0;

                  const linkStyle = {
                    ...S.sbLinkBase,
                    color: active ? C.sbWhite : (locked ? C.sbTextFaded : C.sbText),
                    background: active ? sbActiveBg : 'transparent',
                    cursor: locked ? 'default' : 'pointer',
                  };

                  return (
                    <Link
                      key={item.path}
                      to={locked ? '#' : item.path}
                      style={linkStyle}
                      onMouseEnter={e => { if (!active && !locked) e.currentTarget.style.background = C.sbHover; }}
                      onMouseLeave={e => { if (!active && !locked) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {active && <span style={sbActiveBar} />}
                      <Icon name={item.icon} size={16} />
                      <span>{item.label}</span>
                      {orphanCount > 0 ? (
                        <span style={{ ...S.sbCount, color: '#f87171', fontWeight: 600 }}>{orphanCount}</span>
                      ) : item.path === '/dossiers' && dossiersCount != null ? (
                        <span style={{ ...S.sbCount, color: active ? C.sbWhite : C.sbTextDim, fontWeight: active ? 600 : 400 }}>{dossiersCount}</span>
                      ) : item.badge ? (
                        <span style={{ ...S.sbCount, color: brandPrimary, fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{item.badge}</span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            );
          })}

          {/* Section Équipe Lexback (dynamique) */}
          {lexbackStaff.length > 0 && (
            <div style={S.sbSection}>
              <div style={S.sbSectionTitle}>Équipe Lexback</div>
              {lexbackStaff.map((member) => {
                const isIris = member.type === 'ai' || member.is_ai;
                return (
                  <div key={member.id || member.prenom} style={S.sbStaffRow}>
                    {isIris ? (
                      <div style={S.sbStaffIris}>
                        <Icon name="sparkles" size={12} />
                      </div>
                    ) : (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{ ...S.sbStaffAvatar, background: _avatarColor(member.prenom || member.name) }} />
                        {member.online && <span style={S.sbStaffOnline} />}
                      </div>
                    )}
                    <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {member.prenom || member.name}
                    </span>
                    {isIris && <span style={S.sbStaffIrisBadge}>IA</span>}
                    {member.dossiers_count != null && (
                      <span style={S.sbCount}>{member.dossiers_count}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Bottom : user + menu account */}
          <div style={S.sbBottom}>
            <div style={{ position: 'relative' }} ref={menuRef}>
              <button
                type="button"
                onClick={() => { setMenuOpen(o => !o); setNotifOpen(false); }}
                style={{ ...S.sbUser, background: menuOpen ? C.sbHover : 'transparent', border: 'none', width: '100%', fontFamily: 'inherit' }}
                onMouseEnter={e => { if (!menuOpen) e.currentTarget.style.background = C.sbHover; }}
                onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = 'transparent'; }}
                aria-label="Menu compte"
                aria-expanded={menuOpen}
              >
                <div style={{ ...S.sbUserAvatar, background: brandPrimary }}>{initials}</div>
                <div style={S.sbUserInfo}>
                  <div style={S.sbUserName}>{user?.prenom} {user?.nom}</div>
                  <div style={S.sbUserMeta}>Plan {planLabel}</div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.sbTextDim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </button>

              {menuOpen && (
                <div style={{ ...S.menu, bottom: 'calc(100% + 8px)', top: 'auto', right: 'auto', left: 0 }} role="menu">
                  <div style={S.menuHeader}>
                    <div style={S.menuWsName}>{workspaceName}</div>
                    <div style={S.menuWsPlan}>Plan {planLabel}</div>
                  </div>

                  {NAV_SECONDARY.map((item, i) => {
                    if (item.section) {
                      return <div key={`s-${i}`} style={S.menuSection}>{item.section}</div>;
                    }
                    const itemLevel = PLAN_LEVELS[item.minPlan] || 1;
                    const locked = planLevel < itemLevel;
                    const active = isActive(item.path);
                    const itemStyle = {
                      ...S.menuItem,
                      background: active ? C.goldBg : 'transparent',
                      color: active ? C.goldText : locked ? C.text3 : C.text2,
                      fontWeight: active ? 500 : 400,
                      cursor: locked ? 'default' : 'pointer',
                    };
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        style={{ textDecoration: 'none' }}
                        onClick={() => setMenuOpen(false)}
                      >
                        <div
                          style={itemStyle}
                          onMouseEnter={e => {
                            if (!active && !locked) {
                              e.currentTarget.style.background = C.borderLt;
                              e.currentTarget.style.color = C.text;
                            }
                          }}
                          onMouseLeave={e => {
                            if (!active && !locked) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = C.text2;
                            }
                          }}
                        >
                          <span style={{ flex: 1 }}>{item.label}</span>
                          {item.badge && (
                            <span style={active ? S.badgeActive : S.badge}>{item.badge}</span>
                          )}
                        </div>
                      </Link>
                    );
                  })}

                  {UPGRADE_MSG[plan] && (
                    <div
                      style={S.menuUpgrade}
                      onClick={() => { setMenuOpen(false); navigate('/facturation'); }}
                    >
                      <div style={S.menuUpgradeTitle}>{UPGRADE_MSG[plan].title}</div>
                      <div style={S.menuUpgradeSub}>{UPGRADE_MSG[plan].sub}</div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); handleLogout(); }}
                    style={S.menuLogout}
                    onMouseEnter={e => { e.currentTarget.style.background = C.borderLt; e.currentTarget.style.color = C.text; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.text2; }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Se déconnecter
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* ============================================================ */}
        {/* CONTENU PRINCIPAL (décalé du width de la sidebar)            */}
        {/* ============================================================ */}
        <div style={{
          flex: 1,
          minWidth: 0,
          marginLeft: `${SIDEBAR_WIDTH}px`,
          display: 'flex',
          flexDirection: 'column',
        }}>

          {/* Topbar fine (bell + recap) */}
          <header style={S.topbar}>
            <div style={{ position: 'relative' }} ref={notifRef}>
              <button
                type="button"
                onClick={() => { setNotifOpen(o => !o); setMenuOpen(false); }}
                style={{ ...S.tbBellWrap, background: notifOpen ? C.borderLt : 'transparent' }}
                aria-label="Notifications"
                aria-expanded={notifOpen}
                onMouseEnter={e => { if (!notifOpen) e.currentTarget.style.background = C.borderLt; }}
                onMouseLeave={e => { if (!notifOpen) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon name="bell" size={18} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '4px', right: '4px',
                    background: '#b3261e', color: '#fff',
                    fontSize: '9px', fontWeight: 700,
                    padding: '1px 5px', borderRadius: '99px',
                    minWidth: '14px', textAlign: 'center', lineHeight: 1.3,
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div style={S.notifPanel} role="dialog" aria-label="Notifications">
                  <div style={S.notifHeader}>
                    <div style={S.notifTitle}>
                      Notifications
                      {unreadCount > 0 && (
                        <span style={{ marginLeft: '8px', fontSize: '11.5px', color: C.text3, fontWeight: 400 }}>
                          {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button type="button" onClick={markAllNotifRead} style={S.notifMarkAll}>
                        Tout marquer comme lu
                      </button>
                    )}
                  </div>

                  <div style={S.notifList}>
                    {notifs.length === 0 ? (
                      <div style={S.notifEmpty}>Aucune notification pour le moment.</div>
                    ) : (
                      notifs.map((n) => {
                        const meta = NOTIF_TYPE_META[n.type] || NOTIF_TYPE_META.default;
                        const unread = !n.read_at;
                        return (
                          <div
                            key={n.id}
                            onClick={() => handleNotifClick(n)}
                            style={{
                              ...S.notifItem,
                              background: unread ? 'rgba(217,168,71,0.04)' : 'transparent',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#faf7f1'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = unread ? 'rgba(217,168,71,0.04)' : 'transparent'; }}
                          >
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                              <div style={{
                                width: '28px', height: '28px', borderRadius: '50%',
                                background: _avatarColor(n.triggered_by_name),
                                color: 'white', fontSize: '10px', fontWeight: 600,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>{_initials(n.triggered_by_name)}</div>
                              <span style={{
                                position: 'absolute', bottom: '-2px', right: '-2px',
                                width: '14px', height: '14px', borderRadius: '50%',
                                background: '#fff', border: `1.5px solid ${meta.color}`,
                                fontSize: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>{meta.icon}</span>
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '12.5px', color: C.text, lineHeight: 1.4 }}>
                                <strong style={{ fontWeight: 600 }}>{n.triggered_by_name || 'Système'}</strong>
                                <span style={{ color: C.text2 }}> {meta.label}</span>
                              </div>
                              {n.payload?.dossier_societe && (
                                <div style={{ fontSize: '11px', color: C.text3, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  <span style={{ fontFamily: 'JetBrains Mono, monospace', color: C.gold }}>{n.payload.dossier_reference}</span>
                                  <span> · </span>
                                  <span>{n.payload.dossier_societe}</span>
                                </div>
                              )}
                              <div style={{ fontSize: '10.5px', color: C.text3, marginTop: '3px' }}>
                                {_relativeDate(n.created_at)}
                              </div>
                            </div>

                            {unread && (
                              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: C.gold, flexShrink: 0, marginTop: '8px' }} />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div style={S.notifFooter}>
                    <Link
                      to="/notifications"
                      style={{ fontSize: '12px', color: C.gold, fontWeight: 500, textDecoration: 'none' }}
                      onClick={() => setNotifOpen(false)}
                    >
                      Voir toutes les notifications →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </header>

          {/* Main content */}
          <main style={{
            flex: 1,
            overflow: 'auto',
            padding: '28px',
            maxWidth: '1400px',
            width: '100%',
            margin: '0 auto',
            minWidth: 0,
          }}>
            {children}
          </main>
        </div>

        {/* ============================================================ */}
        {/* FAB Lexa — assistante IA en bas à droite                     */}
        {/* ============================================================ */}
        <LexaFAB />

      </div>
    </>
  );
}
